"use client";

import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCustomEmojis, createCustomEmoji } from "@/lib/api/emojis";
import { useUpload } from "@/lib/hooks/useUpload";
import { useDismissable } from "@/lib/hooks/useDismissable";
import { customEmojisKey, invalidateCustomEmojis } from "@/lib/hooks/useGroupPage";
import { Input } from "./ui/Input";
import { EmojiGrid } from "./EmojiGrid";
import { EmojiPickerSheet } from "./EmojiPickerSheet";
import { emojiFileError, sanitizeEmojiName, slugifyEmojiName } from "@/lib/emoji/upload";

interface EmojiPickerProps {
  groupId: number;
  /** Called with the exact text to insert at the caret — `:shortcode:` for a
   * custom emoji, the literal unicode character for a standard one. */
  onSelect: (text: string) => void;
  canAdd?: boolean;
  groupSlug?: string;
}

export function EmojiPicker({ groupId, onSelect, canAdd }: EmojiPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  // Decided once, at the moment the picker opens (not reactively): below the
  // `sm` breakpoint it renders as a near-full-height bottom sheet instead of
  // the small anchored popover, and its own search box skips autoFocus there
  // so opening the picker doesn't immediately summon the mobile keyboard.
  const [isMobile, setIsMobile] = useState(false);
  // Escape closes the picker — and, via `useDismissable`, is consumed doing
  // it. Do not put `escape: false` back: it was inherited from the hand-rolled
  // effect this was migrated from, which had no Escape handling at all, and it
  // meant that once `Dialog` learned to close on Escape (OSO-402) one keystroke
  // went past the open picker and asked whether to discard the post.
  const ref = useDismissable<HTMLDivElement>(open, close);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: customEmojisKey(groupId),
    queryFn: () => getCustomEmojis(groupId),
    enabled: open,
  });

  const emojis = data?.items ?? [];

  // Reset at the point of closing rather than in an effect watching `open` —
  // `open` is local state, so every path that closes the picker goes through
  // here and the panel never re-opens holding a stale search or add form.
  function close() {
    setOpen(false);
    setAdding(false);
    setSearch("");
  }

  function toggleOpen() {
    if (open) {
      close();
      return;
    }
    // (jsdom has no matchMedia; assume desktop when we can't tell.)
    setIsMobile(
      typeof window.matchMedia === "function" &&
        window.matchMedia("(max-width: 639px)").matches
    );
    setOpen(true);
  }

  const panel = (
    <>
      {!adding && (
        <>
          <div className="p-2 border-b border-border">
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Søg emoji..."
              autoFocus={!isMobile}
              className="w-full bg-surface-2 border border-border rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div className={isMobile ? "p-2" : "max-h-72 overflow-y-auto p-2"}>
            <EmojiGrid
              customEmojis={emojis}
              search={search}
              onSelectStandard={(emoji) => { onSelect(emoji.char); close(); }}
              onSelectCustom={(emoji) => { onSelect(`:${emoji.shortcode}:`); close(); }}
              customEmptyMessage={canAdd ? "Ingen emojis endnu — tilføj den første herunder" : "Ingen emojis i denne gruppe endnu"}
            />
          </div>

          {emojis.length > 0 && !canAdd && (
            <div className="px-2 py-1.5 border-t border-border bg-surface-2">
              <p className="text-[10px] text-subtle-foreground text-center">
                Skriv :shortcode: i teksten for at indsætte
              </p>
            </div>
          )}

          {canAdd && (
            <div className="px-2 py-1.5 border-t border-border">
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="w-full flex items-center justify-center gap-1.5 text-xs text-primary hover:text-indigo-700 dark:hover:text-indigo-300 font-medium py-0.5"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Tilføj emoji
              </button>
            </div>
          )}
        </>
      )}

      {adding && (
        <UploadForm
          groupId={groupId}
          onDone={() => {
            setAdding(false);
            invalidateCustomEmojis(queryClient, groupId);
          }}
          onCancel={() => setAdding(false)}
        />
      )}
    </>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggleOpen}
        className="w-9 h-9 rounded-full hover:bg-amber-50 dark:hover:bg-amber-900/30 flex items-center justify-center text-amber-500 transition-colors"
        title="Brugerdefinerede emojis"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <line x1="9" y1="9" x2="9.01" y2="9" />
          <line x1="15" y1="9" x2="15.01" y2="9" />
        </svg>
      </button>

      {open && (
        isMobile ? (
          <EmojiPickerSheet onClose={close}>{panel}</EmojiPickerSheet>
        ) : (
          <div className="absolute bottom-full mb-2 right-0 w-64 sm:w-72 bg-surface rounded-xl border border-border shadow-lg z-50">
            {panel}
          </div>
        )
      )}
    </div>
  );
}

function UploadForm({ groupId, onDone, onCancel }: { groupId: number; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  const [uploadId, setUploadId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const upload = useUpload();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    // CHECKED HERE TOO, not just on the admin page. This form had neither
    // check, so it would upload a 4 MB photo or a PDF and only learn better
    // from the server's refusal.
    const fileError = emojiFileError(file);
    if (fileError) {
      setError(fileError);
      return;
    }

    setPreview(URL.createObjectURL(file));
    setError(null);
    if (!name) setName(slugifyEmojiName(file.name));
    try {
      const result = await upload.mutateAsync({ file, scope: { type: "emoji", id: groupId } });
      setUploadId(result.id);
    } catch {
      setPreview(null);
      setError("Upload fejlede");
    }
  };

  const handleSubmit = async () => {
    if (!uploadId || !name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await createCustomEmoji(groupId, { uploadId, shortcode: name.trim() });
      onDone();
    } catch {
      setError("Kunne ikke oprette — tjek at navnet er unikt");
      setSubmitting(false);
    }
  };

  return (
    <div className="p-3">
      <p className="text-xs font-medium text-foreground mb-3">Tilføj emoji</p>
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-10 h-10 shrink-0 rounded-lg border-2 border-dashed border-border-strong hover:border-indigo-400 flex items-center justify-center overflow-hidden transition-colors"
        >
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-contain" />
          ) : upload.isPending ? (
            <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-subtle-foreground">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          )}
        </button>
        <input ref={fileInputRef} type="file" className="hidden" accept="image/*" onChange={handleFile} />
        <div className="flex items-center gap-0.5 flex-1 min-w-0 bg-surface-2 border border-border rounded px-2 py-1.5">
          <span className="text-xs text-subtle-foreground">:</span>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(sanitizeEmojiName(e.target.value))}
            placeholder="navn"
            className="flex-1 min-w-0 text-xs bg-transparent text-foreground placeholder:text-subtle-foreground focus:outline-none"
          />
          <span className="text-xs text-subtle-foreground">:</span>
        </div>
      </div>
      {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!uploadId || !name.trim() || submitting}
          className="flex-1 text-xs font-medium py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? "Opretter..." : "Tilføj"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-muted-foreground hover:text-gray-700 dark:hover:text-gray-200 px-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
        >
          Annullér
        </button>
      </div>
    </div>
  );
}
